import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge, ListGroup, Spinner } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaArrowLeft, FaShoppingCart, FaExclamationTriangle, FaQrcode } from 'react-icons/fa';
import axios from 'axios';

const ArtikelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artikel, setArtikel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartMessage, setCartMessage] = useState('');
  const [cartMessageType, setCartMessageType] = useState('success');

  // Artikel laden
  useEffect(() => {
    const fetchArtikel = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/api/artikel/${id}`);
        setArtikel(response.data);
      } catch (err) {
        console.error('Fehler beim Laden des Artikels:', err);
        setError('Der Artikel konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchArtikel();
  }, [id]);

  // Artikel löschen
  const handleDelete = async () => {
    if (window.confirm(`Möchten Sie den Artikel "${artikel.name}" wirklich löschen?`)) {
      try {
        await axios.delete(`/api/artikel/${id}`);
        navigate('/artikel', { state: { message: `Artikel "${artikel.name}" wurde gelöscht.` } });
      } catch (err) {
        console.error('Fehler beim Löschen des Artikels:', err);
        setError('Der Artikel konnte nicht gelöscht werden. Er wird möglicherweise noch in einer Bestellung verwendet.');
      }
    }
  };

  // Zum Warenkorb hinzufügen
  const addToCart = async (menge = 1) => {
    try {
      await axios.post('/api/warenkorb', { artikelId: artikel.id, menge });
      setCartMessage(`${menge}x "${artikel.name}" wurde zum Warenkorb hinzugefügt.`);
      setCartMessageType('success');
      
      // Nachricht nach 3 Sekunden ausblenden
      setTimeout(() => setCartMessage(''), 3000);
    } catch (err) {
      console.error('Fehler beim Hinzufügen zum Warenkorb:', err);
      setCartMessage('Der Artikel konnte nicht zum Warenkorb hinzugefügt werden.');
      setCartMessageType('danger');
    }
  };

  // QR-Code für den Artikel generieren (basierend auf Artikel-ID)
  const getQrCodeUrl = () => {
    // Vollständige URL mit Host und Pfad für den QR-Code verwenden
    const baseUrl = window.location.origin;
    const artikelUrl = `${baseUrl}/artikel/${artikel.id}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(artikelUrl)}`;
  };

  // Direkte URL zum Artikel
  const getArtikelUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/artikel/${artikel.id}`;
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Laden...</span>
        </Spinner>
        <p className="mt-2">Artikel wird geladen...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          {error}
          <div className="mt-3">
            <Button as={Link} to="/artikel" variant="outline-primary">
              <FaArrowLeft className="me-2" /> Zurück zur Artikelliste
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!artikel) {
    return (
      <Container>
        <Alert variant="warning">
          Artikel nicht gefunden.
          <div className="mt-3">
            <Button as={Link} to="/artikel" variant="outline-primary">
              <FaArrowLeft className="me-2" /> Zurück zur Artikelliste
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{artikel.name}</h1>
        <Button 
          as={Link} 
          to="/artikel" 
          variant="outline-secondary"
          className="d-none d-md-block"
        >
          <FaArrowLeft className="me-2" /> Zurück
        </Button>
      </div>
      
      {cartMessage && (
        <Alert variant={cartMessageType} dismissible onClose={() => setCartMessage('')}>
          {cartMessage}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col lg={8} className="mb-4 mb-lg-0">
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Artikeldetails</h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Kategorie:</Col>
                  <Col sm={8}>{artikel.kategorie || '-'}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Lieferant:</Col>
                  <Col sm={8}>{artikel.lieferant || '-'}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Lagerbestand:</Col>
                  <Col sm={8}>
                    {artikel.lagerbestand} Stk.
                    {artikel.lagerbestand === 0 && (
                      <Badge bg="danger" text="white" className="ms-2">
                        <FaExclamationTriangle className="me-1" /> Nicht vorrätig
                      </Badge>
                    )}
                    {artikel.lagerbestand > 0 && artikel.lagerbestand < 5 && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        <FaExclamationTriangle className="me-1" /> Niedriger Bestand
                      </Badge>
                    )}
                  </Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Online-Shop:</Col>
                  <Col sm={8}>
                    {artikel.onlineshoplink ? (
                      <a href={artikel.onlineshoplink} target="_blank" rel="noopener noreferrer">
                        {artikel.onlineshoplink}
                      </a>
                    ) : (
                      '-'
                    )}
                  </Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Zuletzt aktualisiert:</Col>
                  <Col sm={8}>
                    {new Date(artikel.updated_at).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Col>
                </Row>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Aktionen</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="success" 
                  className="d-flex align-items-center justify-content-center"
                  onClick={() => addToCart(1)}
                >
                  <FaShoppingCart className="me-2" /> Zum Warenkorb hinzufügen
                </Button>
                <Button 
                  as={Link} 
                  to={`/artikel/${artikel.id}/bearbeiten`}
                  variant="primary"
                  className="d-flex align-items-center justify-content-center"
                >
                  <FaEdit className="me-2" /> Artikel bearbeiten
                </Button>
                <Button 
                  variant="danger" 
                  className="d-flex align-items-center justify-content-center"
                  onClick={handleDelete}
                >
                  <FaTrash className="me-2" /> Artikel löschen
                </Button>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">QR-Code</h5>
            </Card.Header>
            <Card.Body className="text-center">
              <img 
                src={getQrCodeUrl()} 
                alt={`QR-Code für ${artikel.name}`} 
                className="img-fluid mb-3" 
                style={{ maxWidth: '200px' }}
              />
              <p className="mb-1 small">QR-Code für einfache Erfassung</p>
              <p className="text-muted small">Direkte URL:</p>
              <p className="small bg-light p-2 rounded text-break">{getArtikelUrl()}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Button 
        as={Link} 
        to="/artikel" 
        variant="outline-secondary"
        className="d-block d-md-none mb-4"
      >
        <FaArrowLeft className="me-2" /> Zurück zur Artikelliste
      </Button>
    </Container>
  );
};

export default ArtikelDetail;
